import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { useStoredState } from '@/hooks';
import { ApiRequestError } from '@/lib/api-client';
import { logger } from '@/lib/logger';
import type { ClinicUser } from '@/types/api';
import { type Doctor, doctorsFor, pickDoctor } from '@/utils/doctors';
import { settingsApi } from '../settings/api';

type DoctorState = {
  /** The doctors this user records for (the selector's options). */
  doctors: Doctor[];
  /** Every active doctor of the clinic (the calendar filter). */
  all: Doctor[];
  /** The doctor new entries and dictations are recorded for. */
  doctorId: string | null;
  setDoctorId: (id: string) => void;
  /** The calendar's doctor filter (null: all doctors), remembered per user. The top bar sets it. */
  filter: string | null;
  setFilter: (id: string | null) => void;
  /** Reload the clinic's doctors (after a user was added or changed). */
  refresh: () => void;
};

const DoctorContext = createContext<DoctorState | null>(null);

/**
 * The doctor selector (it replaced the chair selector). The clinic's doctors
 * are kept on the device, so the selector also works offline.
 */
export function DoctorProvider({ user, children }: { user: ClinicUser; children: ReactNode }) {
  const [all, setAll] = useStoredState<Doctor[]>(`dentlog.doctors.${user.clinic.id}`, []);
  // Per user: on a shared tablet one person's pick must not carry over to the next.
  const [stored, setDoctorId] = useStoredState<string | null>(`dentlog.doctor.${user.id}`, null);
  const [picked, setFilter] = useStoredState<string | null>(
    `dentlog.calendarFilter.${user.id}`,
    null
  );

  const refresh = useCallback(() => {
    settingsApi
      .users()
      .then((users) =>
        setAll(
          users.filter((u) => u.treatsPatients && u.active).map((u) => ({ id: u.id, name: u.name }))
        )
      )
      .catch((err) => {
        if (!(err instanceof ApiRequestError && err.isNetwork)) logger.warn('doctors failed', err);
      });
  }, [setAll]);

  useEffect(refresh, [refresh]);

  const value = useMemo(() => {
    const doctors = doctorsFor(user, all);
    // A doctor who left the clinic no longer filters the calendar.
    const filter = picked && all.some((d) => d.id === picked) ? picked : null;
    return {
      doctors,
      all,
      doctorId: pickDoctor(user, doctors, stored),
      setDoctorId,
      filter,
      setFilter,
      refresh,
    };
  }, [user, all, stored, setDoctorId, picked, setFilter, refresh]);
  return <DoctorContext.Provider value={value}>{children}</DoctorContext.Provider>;
}

export function useDoctor(): DoctorState {
  const ctx = useContext(DoctorContext);
  if (!ctx) throw new Error('useDoctor outside DoctorProvider');
  return ctx;
}
